import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
// Update import paths
import NetworkGraph from './components/NetworkGraph/NetworkGraph';
import FilterPanel from './components/FilterPanel/FilterPanel';
import StatisticsPanel from './components/StatisticsPanel/StatisticsPanel';
import NodeDetail from './components/NodeDetail/NodeDetail';
import SummaryPanel from './components/SummaryPanel/SummaryPanel'; // New Summary Panel
import HeatmapPanel from './components/HeatmapPanel/HeatmapPanel'; // Industry×Topics heatmap
import UmapPanel from './components/UmapPanel/UmapPanel'; // UMAP 2D scatter
import DataViewerPanel from './components/DataViewerPanel/DataViewerPanel'; // In-app data viewer
import Login from './components/Login'; // Import login component
import ErrorBoundary from './components/ErrorBoundary'; // Error boundary
import HelpPanel from './components/HelpPanel/HelpPanel'; // Contextual help system
import InsightCards from './components/InsightCards/InsightCards'; // Insight layer: passive insight cards
import ChatDrawer from './components/ChatDrawer/ChatDrawer'; // Insight layer: active chat drawer
import DocumentViewer from './components/DocumentViewer/DocumentViewer'; // Source document viewer (opt-in)
import TopicDocumentsPanel from './components/TopicDocumentsPanel/TopicDocumentsPanel'; // Topic → ranked documents picker
import CellRecordsModal from './components/CellRecordsModal/CellRecordsModal'; // Heatmap cell → records table
import OverviewPanel from './components/OverviewPanel/OverviewPanel'; // Filter-aware dashboard overview
import StrategyPanel from './components/StrategyPanel/StrategyPanel'; // Strategy document tab
import { getThemeColors, setClientIndustryColors } from './utils/colorConstants';
import './App.css';

const APP_BRAND = process.env.REACT_APP_BRAND_LABEL || '';

// Helper function to transform summary data for the SummaryPanel
// Helper function to transform summary data for the SummaryPanel
const transformSummaryData = (networkData, summaryRaw) => {
  if (!summaryRaw) {
    console.log("No summary data received from API");
    return null;
  }
  
  console.log("Transforming summary data:", summaryRaw);
  
  // Convert {key: count} objects to arrays of objects with label and count
  const convertToArray = (obj) => {
    if (!obj || typeof obj !== 'object') return [];
    return Object.entries(obj).map(([label, count]) => ({ label, count }));
  };

  return {
    totalProposals: summaryRaw.totalProposals || 0,
    totalUniqueBeamlines: summaryRaw.totalUniqueBeamlines || 0,
    totalUniqueAffiliations: summaryRaw.totalUniqueAffiliations || 0,
    totalUniqueOrgs: summaryRaw.totalUniqueOrgs || 0,
    totalUniqueIndustries: summaryRaw.totalUniqueIndustries || 0,
    totalUniqueTopics: summaryRaw.totalUniqueTopics || 0,
    beamlines: convertToArray(summaryRaw.beamlines),
    affiliations: convertToArray(summaryRaw.affiliations),
    industries: convertToArray(summaryRaw.industries),
    topics: convertToArray(summaryRaw.topics)
  };
};

// Define the API base URL
// Prioritize REACT_APP_API_URL if set (e.g., in Azure Static Web Apps environment variables).
// Fallback to an empty string for local development (to use the proxy in package.json).
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check for existing auth on mount
  useEffect(() => {
    const authToken = sessionStorage.getItem('nexus_token');
    if (authToken) {
      setIsAuthenticated(true);
    }
  }, []);

  // Handle login
  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  // Handle auth failure (token expired/invalid)
  const handleAuthFailure = () => {
    sessionStorage.removeItem('nexus_token');
    setIsAuthenticated(false);
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  // Main app content (only shown when authenticated)
  return <AuthenticatedApp onAuthFailure={handleAuthFailure} />;
}

// Wrapper around fetch that adds JWT Authorization header
function authFetch(url, options = {}) {
  const token = sessionStorage.getItem('nexus_token');
  const headers = { ...(options.headers || {}) };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, headers });
}

function AuthenticatedApp({ onAuthFailure }) {
  useEffect(() => {
    if (APP_BRAND) {
      document.title = APP_BRAND;
    }
  }, []);

  // State for selected filters - Default to an "ALL" state
  // Multi-select filters use arrays, single-select use strings
  const [filters, setFilters] = useState({
    industry: [], // Multi-select for industries
    topics: [], // Multi-select for topics
    status: [], // Multi-select for statuses (was single)
    timeFrame: [], // Default timeFrame, will be updated based on availableDatePoints
    orgType: [], // Multi-select for organization types (was single)
    organisation: [], // Multi-select for specific organisations
    collaborationType: [], // Multi-select for collaboration type (predicted_collaboration_type)
    unspecifiedMode: 'on', // Tri-state: 'on' (include all), 'off' (exclude unspecified), 'only' (only unspecified)
    networkView: null,     // Active multilayer network view id (null = schema default)
    peopleAxis: 'affiliation', // People axis for multilayer views: 'affiliation' | 'author'
    requireCluster: true   // Restrict multilayer views to records matched to an Excellence Cluster
  });
  
  // State for loaded data
  const [networkData, setNetworkData] = useState(null);
  const pendingSliderMaxRef = useRef(false); // Flag: set slider to max after next real data load
  const useCaseRunRef = useRef(false); // Flag: suppress duplicate analysis runs during use-case load
  const [summaryData, setSummaryData] = useState(null); // Replaces topicData and topicDistributionData
  const [globalSummaryData, setGlobalSummaryData] = useState(null); // Store global summary for restoration
  const [globalHeatmapData, setGlobalHeatmapData] = useState(null); // Store global heatmap for restoration
  const [globalUmapData, setGlobalUmapData] = useState(null); // Store global UMAP for restoration
  const [statistics, setStatistics] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState([]); // Multi-select: array of node detail objects
  const [selectedNodeIds, setSelectedNodeIds] = useState(new Set()); // Multi-select: Set of selected node IDs
  const selectedNodeIdsRef = useRef(new Set()); // Ref mirror for async race-condition guards
  const [neighborNodeIds, setNeighborNodeIds] = useState(new Set()); // State for neighbor IDs
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // New state for file upload and analysis
  const [fileUploaded, setFileUploaded] = useState(false);
  const [fileName, setFileName] = useState('');
  const [analysisStatus, setAnalysisStatus] = useState('');
  const [progressPollingInterval, setProgressPollingInterval] = useState(null);

  const [theme, setTheme] = useState('autumn'); // Add theme state ('default' or 'autumn')
  const [activeVizTab, setActiveVizTab] = useState('network'); // 'overview' | 'network' | 'heatmap' | 'data'
  const [heatmapData, setHeatmapData] = useState(null); // { rows, columns, matrix, maxValue }
  const [heatmapAxes, setHeatmapAxes] = useState({ y: 'industry', x: 'topic' }); // user-selectable heatmap axes
  const heatmapAxesRef = useRef(heatmapAxes);
  useEffect(() => { heatmapAxesRef.current = heatmapAxes; }, [heatmapAxes]);
  const [umapData, setUmapData] = useState(null); // { points, industries, topics }
  const [collapsedSection, setCollapsedSection] = useState(null); // null | 'heatmap' | 'umap'
  const [summaryPanelWidth, setSummaryPanelWidth] = useState(440); // Resizable right panel width
  const [summaryCollapsed, setSummaryCollapsed] = useState(false); // Collapse summary panel to thin strip
  const [exporting, setExporting] = useState(false); // Export in progress
  const [hideOrgLabels, setHideOrgLabels] = useState(false); // Hide company labels on network graph
  const [showIndustryOverlay, setShowIndustryOverlay] = useState(false); // Industry aura overlay on network graph
  const [minLinkWeight, setMinLinkWeight] = useState(1); // Connection strength slider (lifted from NetworkGraph)
  const [helpOpen, setHelpOpen] = useState(false); // Help panel open state
  const [helpActiveSection, setHelpActiveSection] = useState(null); // Deep-link section for help panel
  const [features, setFeatures] = useState(null); // null = loading (show all); populated from /api/config
  const [schemaConfig, setSchemaConfig] = useState(null); // {key, labels, facets} from /api/config
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false); // Insight: chat drawer open state
  const [insightEnabled, setInsightEnabled] = useState(false); // Global insight layer toggle
  const [viewerDoc, setViewerDoc] = useState(null); // {filename, paraIdx} | null — source document viewer state
  // Which floating layer is in front when both the chat drawer and the document
  // viewer are open: 'chat' | 'doc'. Clicking either brings it forward.
  const [frontLayer, setFrontLayer] = useState('doc');
  const [topicDocsRequest, setTopicDocsRequest] = useState(null); // {topic, industry} | null — topic-documents modal state
  const [cellRecordsScope, setCellRecordsScope] = useState(null); // {yAxis,yValue,xAxis,xValue,yLabel,xLabel} | null
  const [topicViews, setTopicViews] = useState([]); // [{id,label,description}]
  const [topicView, setTopicView] = useState(''); // active view id; '' = all topics
  // Schema label for the topic axis, e.g. 'Topics' (A2A) or 'WoS Category' (MARVIN).
  const topicLabelRaw = schemaConfig?.labels?.topic || 'Topic';
  const topicLabelSingular = /[^s]s$/i.test(topicLabelRaw) ? topicLabelRaw.slice(0, -1) : topicLabelRaw;
  const topicLabelPlural = /s$/i.test(topicLabelRaw) ? topicLabelRaw
    : /y$/i.test(topicLabelRaw) ? topicLabelRaw.slice(0, -1) + 'ies' : topicLabelRaw + 's';
  const topicViewRef = useRef('');
  useEffect(() => { topicViewRef.current = topicView; }, [topicView]);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(0);
  const [availableDatePoints, setAvailableDatePoints] = useState([]); // New state for dynamic date points
  
  // Available options for multi-select filters (populated from data)
  const [availableIndustries, setAvailableIndustries] = useState([]);
  const [availableTopics, setAvailableTopics] = useState([]);
  const [availableThemes, setAvailableThemes] = useState([]);
  const [availableOrganisations, setAvailableOrganisations] = useState([]);
  const [availableCollaborationTypes, setAvailableCollaborationTypes] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  
  // Dynamic filter options based on current selections
  const [dynamicFilterOptions, setDynamicFilterOptions] = useState({
    industries: [],
    topics: [],
    statuses: [],
    orgTypes: [],
    collaborationTypes: []
  });

  // Destructure non-slider filters for the effect dependency array
  const { status, orgType, industry, topics, organisation, collaborationType, unspecifiedMode } = filters;

  // *** Check data status on mount ***
  useEffect(() => {
    console.log("Checking data status on mount...");
    authFetch(`${API_BASE_URL}/api/get-data-status`)
      .then(response => {
        if (response.status === 401) { onAuthFailure(); return; }
        if (!response.ok) {
          throw new Error(`Data status check failed with status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log("Data status check successful:", data);
        if (data.status === 'loaded') {
          setFileUploaded(true);
          setFileName('Pre-loaded data');
          setAnalysisStatus('Data loaded from server. Ready to run analysis.');
          if (data.unique_time_frames && Array.isArray(data.unique_time_frames)) {
            setAvailableDatePoints(data.unique_time_frames);
          }
          // Populate available industries and topics if provided
          if (data.availableIndustries) setAvailableIndustries(data.availableIndustries);
          if (data.availableTopics) setAvailableTopics(data.availableTopics);
          if (data.availableThemes) setAvailableThemes(data.availableThemes);
          if (data.availableOrganisations) setAvailableOrganisations(data.availableOrganisations);
          if (data.availableCollaborationTypes) setAvailableCollaborationTypes(data.availableCollaborationTypes);
          if (data.availableStatuses) setAvailableStatuses(data.availableStatuses);
        }
      })
      .catch(error => {
        console.error("Data status check failed:", error);
      });
  }, []); // Empty dependency array ensures this runs only once on mount

  // Fetch feature flags from backend on mount
  useEffect(() => {
    authFetch(`${API_BASE_URL}/api/config`)
      .then(r => { if (r.status === 401) { onAuthFailure(); return null; } return r.json(); })
      .then(data => {
        if (data?.features) setFeatures(data.features);
        if (data?.industry_colors) setClientIndustryColors(data.industry_colors);
        if (data?.schema) {
          setSchemaConfig(data.schema);
          if (data.schema.heatmapDefaultAxes) {
            setHeatmapAxes(data.schema.heatmapDefaultAxes);
            heatmapAxesRef.current = data.schema.heatmapDefaultAxes;
          }
        }
      })
      .catch(err => console.warn('Could not load /api/config, defaulting to all features:', err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch available topic views (top-50, transversal, stratified) once on mount.
  useEffect(() => {
    authFetch(`${API_BASE_URL}/api/topic-views`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.views) setTopicViews(d.views); })
      .catch(err => console.warn('Could not load /api/topic-views:', err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When the active topic view changes, re-fetch heatmap + UMAP with the
  // current filters so the topic axis updates immediately. Skipped on first
  // render and when no data is loaded yet.
  const didInitTopicView = useRef(false);
  useEffect(() => {
    if (!didInitTopicView.current) { didInitTopicView.current = true; return; }
    if (!dataLoaded) return;
    const body = { filters, view: topicView || undefined, xAxis: heatmapAxes.x, yAxis: heatmapAxes.y };
    authFetch(`${API_BASE_URL}/api/heatmap`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(r => r.ok ? r.json() : null)
      .then(hm => { if (hm) { setHeatmapData(hm); setGlobalHeatmapData(hm); } })
      .catch(err => console.warn('Heatmap view refetch failed:', err));
    authFetch(`${API_BASE_URL}/api/umap`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(r => r.ok ? r.json() : null)
      .then(um => { if (um) { setUmapData(um); setGlobalUmapData(um); } })
      .catch(err => console.warn('UMAP view refetch failed:', err));
  }, [topicView]); // eslint-disable-line react-hooks/exhaustive-deps

  // When features load, ensure activeVizTab is set to a visible tab
  useEffect(() => {
    if (features === null) return;
    const landscapeVisible = features.includes('heatmap') || features.includes('umap');
    if (activeVizTab === 'overview' && !features.includes('overview')) {
      if (landscapeVisible) setActiveVizTab('heatmap');
      else if (features.includes('network')) setActiveVizTab('network');
      else if (features.includes('data')) setActiveVizTab('data');
    } else if (activeVizTab === 'network' && !features.includes('network')) {
      if (features.includes('overview')) setActiveVizTab('overview');
      else if (landscapeVisible) setActiveVizTab('heatmap');
      else if (features.includes('data')) setActiveVizTab('data');
    } else if (activeVizTab === 'heatmap' && !landscapeVisible) {
      if (features.includes('overview')) setActiveVizTab('overview');
      else if (features.includes('network')) setActiveVizTab('network');
      else if (features.includes('data')) setActiveVizTab('data');
    } else if (activeVizTab === 'data' && !features.includes('data')) {
      if (features.includes('overview')) setActiveVizTab('overview');
      else if (features.includes('network')) setActiveVizTab('network');
      else if (landscapeVisible) setActiveVizTab('heatmap');
    }
  }, [features]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper: returns true while features are loading (show everything) or when the key is enabled
  const hasFeature = (key) => features === null || features.includes(key);

  // Help-panel opener — `null` when the manual is disabled via FEATURES,
  // which causes child panels to hide their contextual help (ⓘ) icons.
  const openHelp = hasFeature('help')
    ? (sectionId) => { setHelpActiveSection(sectionId); setHelpOpen(true); }
    : null;

  // ── Source document viewer helpers ─────────────────────────────────
  // Direct open (used when the click surface already knows the filename).
  const openDocument = useCallback((filename, paraIdx = null) => {
    if (!filename) return;
    setFrontLayer('doc');
    setViewerDoc({ filename, paraIdx });
  }, []);

  // Indirect open: ask the backend for a representative doc for a given
  // (industry, topic) cut. Used by heatmap cell clicks, insight "Open source"
  // buttons that only know the topic, etc. Per-cut offset is incremented on
  // each click so repeated opens of the same cell cycle through different
  // high-quality paragraphs instead of always returning the same one.
  const repDocOffsetsRef = useRef(new Map());
  const openRepresentativeDoc = useCallback(({ industry, topic } = {}) => {
    const cutKey = `${industry || ''}|${topic || ''}`;
    const currentOffset = repDocOffsetsRef.current.get(cutKey) || 0;
    const params = new URLSearchParams();
    if (industry) params.set('industry', industry);
    if (topic) params.set('topic', topic);
    params.set('offset', String(currentOffset));
    return authFetch(`${API_BASE_URL}/api/representative-doc?${params.toString()}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => {
        if (d && d.filename) {
          setViewerDoc({ filename: d.filename, paraIdx: d.paraIdx ?? null });
          // Advance the offset so the next click on the same cut shows a
          // different paragraph. Wrap is handled server-side via poolSize.
          repDocOffsetsRef.current.set(cutKey, currentOffset + 1);
        }
      })
      .catch(err => {
        console.warn('No representative document available for this selection:', err.message);
      });
  }, []);

  // Open the Topic → Documents picker: shows a ranked list of documents
  // most relevant to the (topic, industry) slice. Lets the user choose
  // which document to read instead of guessing a single representative
  // paragraph. Backed by /api/topic-documents.
  const openTopicDocuments = useCallback(({ industry, topic } = {}) => {
    if (!topic) return;
    setTopicDocsRequest({ industry: industry || null, topic });
  }, []);

  // Fetcher closure for the modal (kept stable per request so the modal's
  // effect doesn't refire on unrelated re-renders). Forwards current filter
  // state via POST so the document list matches what the user can see
  // elsewhere in the UI (timeFrame, status, organisation, etc.).
  const topicDocsFetcher = useCallback(() => {
    if (!topicDocsRequest) return Promise.resolve({ documents: [], totalDocuments: 0 });
    return authFetch(`${API_BASE_URL}/api/topic-documents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        topic: topicDocsRequest.topic,
        industry: topicDocsRequest.industry || undefined,
        limit: 15,
        filters,
      }),
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); });
  }, [topicDocsRequest, filters]);

  // Fetch dynamic filter options when any filter changes
  useEffect(() => {
    // Only fetch if file is uploaded/data is loaded
    if (!fileUploaded) return;
    
    // Build a filter object excluding the current filter to get available options
    const fetchFilterOptions = async () => {
      try {
        const response = await authFetch(`${API_BASE_URL}/api/get-filter-options`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(filters)
        });
        
        if (response.ok) {
          const options = await response.json();
          setDynamicFilterOptions(options);
        }
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };
    
    // Debounce the fetch to avoid too many requests
    const timeoutId = setTimeout(fetchFilterOptions, 300);
    return () => clearTimeout(timeoutId);
  }, [filters, fileUploaded]);

  // NOTE: Filter-change re-analysis is handled by the debounced [filters] effect below.
  // A previous duplicate effect that watched individual filter fields was removed
  // because it caused handleRunAnalysis() to fire twice on every non-slider filter change.

  // Effect to update filters.timeFrame when availableDatePoints changes or if filters.timeFrame needs validation
  useEffect(() => {
    const newDefaultTimeFrame = (availableDatePoints && availableDatePoints.length > 0)
      ? [availableDatePoints[0], availableDatePoints[availableDatePoints.length - 1]]
      : [];

    const [currentStart, currentEnd] = filters.timeFrame;
    let needsUpdate = false;
    let nextTimeFrame = filters.timeFrame;

    if (availableDatePoints && availableDatePoints.length > 0) {
      // Date points are available.
      const currentIsValid = availableDatePoints.includes(currentStart) &&
                             availableDatePoints.includes(currentEnd) &&
                             availableDatePoints.indexOf(currentStart) <= availableDatePoints.indexOf(currentEnd);

      if (!currentIsValid) {
        // Current timeFrame is invalid (e.g., values not in new points, start > end, or was empty).
        // Set to the new default full range.
        nextTimeFrame = newDefaultTimeFrame;
        // Check if an update is actually needed to avoid unnecessary re-renders/effects
        if (filters.timeFrame.length !== 2 || filters.timeFrame[0] !== nextTimeFrame[0] || filters.timeFrame[1] !== nextTimeFrame[1]) {
          needsUpdate = true;
        }
      }
      // If currentIsValid is true, no update needed based on validity against availableDatePoints.
    } else {
      // No available date points, timeFrame should be empty.
      if (filters.timeFrame.length !== 0) { // Only update if it's not already empty
        nextTimeFrame = [];
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
      setFilters(prevFilters => ({
        ...prevFilters,
        timeFrame: nextTimeFrame
      }));
    }
    // This effect depends on availableDatePoints to react to new data,
    // and filters.timeFrame to re-validate if timeFrame is changed externally (though less common for this specific logic).
    // The primary trigger should be availableDatePoints changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDatePoints, filters.timeFrame]); 

  // Calculate neighbors when selectedNodeIds or networkData changes
  useEffect(() => {
    if (selectedNodeIds.size > 0 && networkData?.edges) {
      const neighbors = new Set();
      networkData.edges.forEach(link => {
        // Ensure link source/target are accessed correctly (might be objects or IDs)
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;

        if (selectedNodeIds.has(sourceId)) {
          neighbors.add(targetId);
        }
        if (selectedNodeIds.has(targetId)) {
          neighbors.add(sourceId);
        }
      });
      // Remove the selected nodes themselves from neighbors
      selectedNodeIds.forEach(id => neighbors.delete(id));
      setNeighborNodeIds(neighbors);
    } else {
      setNeighborNodeIds(new Set());
    }
  }, [selectedNodeIds, networkData]);

  // Auto-run analysis when filters change (after initial data load)
  useEffect(() => {
    // Only auto-run if data has already been loaded (not on initial mount)
    if (dataLoaded && !loading) {
      // Add a small delay to debounce rapid filter changes
      const timeoutId = setTimeout(() => {
        handleRunAnalysis();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Handle node selection - supports multi-select via Ctrl/Cmd+click
  const handleNodeSelect = async (nodeId, isMultiSelect = false) => {
    if (!dataLoaded || !nodeId) {
        // Clearing selection
        setSelectedNodeIds(new Set());
        selectedNodeIdsRef.current = new Set();
        setSelectedNodes([]);
        // Summary restoration is handled by the visibleNodeIds effect
        return;
    };

    // Compute new selected IDs set
    let newSelectedIds;
    if (isMultiSelect) {
      // Toggle: add or remove from existing selection
      newSelectedIds = new Set(selectedNodeIds);
      if (newSelectedIds.has(nodeId)) {
        newSelectedIds.delete(nodeId);
      } else {
        newSelectedIds.add(nodeId);
      }
    } else {
      // Replace: single-select only this node
      newSelectedIds = new Set([nodeId]);
    }

    // If all deselected, clear
    if (newSelectedIds.size === 0) {
      setSelectedNodeIds(new Set());
      selectedNodeIdsRef.current = new Set();
      setSelectedNodes([]);
      return;
    }

    // Set the selected IDs immediately for highlighting
    setSelectedNodeIds(newSelectedIds);
    selectedNodeIdsRef.current = newSelectedIds;

    // Track which fetch batch this is
    const fetchBatchIds = new Set(newSelectedIds);

    try {
      // Build request body with filters + connection-strength context
      const nodeRequestBody = {
        filters,
        ...(visibleNodeIds ? {
          visibleNodeIds: Array.from(visibleNodeIds),
          minLinkWeight: minLinkWeight
        } : {})
      };

      // Fetch details for all selected nodes via multi endpoint
      const nodeIdsArray = Array.from(newSelectedIds);
      const detailsResponse = await authFetch(`${API_BASE_URL}/api/nodes/multi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...nodeRequestBody, nodeIds: nodeIdsArray })
      });

      // Bail if selection changed during fetch
      if (!setsEqual(selectedNodeIdsRef.current, fetchBatchIds)) return;

      if (!detailsResponse.ok) {
        throw new Error(`Node detail response error: ${detailsResponse.status}`);
      }

      const { nodes: nodesData } = await detailsResponse.json();

      // Process each node's data
      const processedNodes = (nodesData || []).map(nodeData => ({
        ...nodeData,
        id: nodeData.id,
        connections: Array.isArray(nodeData.connections) ? nodeData.connections : [],
        proposals: Array.isArray(nodeData.proposals) ? nodeData.proposals : [],
        statistics: nodeData.statistics || {}
      }));

      setSelectedNodes(processedNodes);
      
      // Fetch combined summary data for all selected nodes
      const summaryResponse = await authFetch(`${API_BASE_URL}/api/nodes/multi/summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...nodeRequestBody, nodeIds: nodeIdsArray })
      });

      // Bail if selection changed during fetch
      if (!setsEqual(selectedNodeIdsRef.current, fetchBatchIds)) return;

      if (summaryResponse.ok) {
        const nodeSummaryData = await summaryResponse.json();
        setSummaryData(transformSummaryData(null, nodeSummaryData));
      }
    } catch (error) {
      console.error('Error fetching node details:', error);
      // Only clear selection if these nodes are still selected
      if (setsEqual(selectedNodeIdsRef.current, fetchBatchIds)) {
        setError(`Failed to load node details: ${error.message}`);
        setSelectedNodeIds(new Set());
        selectedNodeIdsRef.current = new Set();
        setSelectedNodes([]);
      }
    }
  };

  // Helper to compare two Sets for equality
  const setsEqual = (a, b) => {
    if (a.size !== b.size) return false;
    for (const item of a) {
      if (!b.has(item)) return false;
    }
    return true;
  };

  // Add a handler to clear the selected node(s) (used by NodeDetail close button)
  const handleClearNodeSelection = () => {
    setSelectedNodeIds(new Set());
    selectedNodeIdsRef.current = new Set();
    setSelectedNodes([]);
    // Summary, heatmap & UMAP restoration is handled by the visibleNodeIds effect
  };

  // Remove a single node from the multi-selection
  const handleDeselectNode = (nodeId) => {
    const newIds = new Set(selectedNodeIds);
    newIds.delete(nodeId);
    if (newIds.size === 0) {
      handleClearNodeSelection();
      return;
    }
    // Update IDs immediately
    setSelectedNodeIds(newIds);
    selectedNodeIdsRef.current = newIds;
    // Remove from the nodes detail array
    setSelectedNodes(prev => prev.filter(n => n.id !== nodeId));
  };

  // Update a multilayer network control (view / people axis / require-cluster)
  // and immediately re-run the analysis with the merged filters.
  const handleNetworkControlChange = (patch) => {
    const merged = { ...filters, ...patch };
    setFilters(merged);
    handleRunAnalysis(merged);
  };

  // Function to handle running the analysis (Network only - no topics)
  const handleRunAnalysis = async (filterOverride = null) => {
    // Guard against being used directly as an event handler (onClick={handleRunAnalysis}),
    // which would pass a cyclic SyntheticEvent here. Only accept a plain filter object.
    const isPlainOverride =
      filterOverride &&
      typeof filterOverride === 'object' &&
      !filterOverride.nativeEvent &&
      !filterOverride.target;
    const effectiveFilters = isPlainOverride ? filterOverride : filters;
    setLoading(true);
    setError(null);
    setNetworkData(null);
    setSummaryData(null); // Clear summary data
    setHeatmapData(null); // Clear heatmap data
    setUmapData(null); // Clear UMAP data
    setStatistics(null);
    setSelectedNodeIds(new Set()); // Clear selection on new analysis
    selectedNodeIdsRef.current = new Set(); // Clear ref to prevent stale async guards
    setSelectedNodes([]); // Close node detail panel
    setNeighborNodeIds(new Set()); // Clear neighbors on new analysis
    // Do not clear availableDatePoints here, let the new data populate it
    
    try {
      // Start polling for progress updates if not already running
      startProgressPolling();
      
      console.log("Filters being sent to /api/run_analysis:", JSON.stringify(effectiveFilters));

      // Request to run analysis - Use relative path
      const response = await authFetch(`${API_BASE_URL}/api/run_analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(effectiveFilters)
      });
      
      if (!response.ok) {
        if (response.status === 401) { onAuthFailure(); return; }
        const errorData = await response.json();
        throw new Error(errorData.message || `Analysis failed: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.status === 'success') {
        console.log("Received network data from /api/run_analysis:", JSON.stringify(result.network));

        // Update availableDatePoints from the backend response
        if (result.unique_time_frames && Array.isArray(result.unique_time_frames)) {
          console.log("Received unique_time_frames:", result.unique_time_frames);
          setAvailableDatePoints(result.unique_time_frames);
        } else {
          console.warn("unique_time_frames not found or not an array in API response.");
          setAvailableDatePoints([]);
        }
        
        // Update available filter options if provided
        if (result.availableIndustries) setAvailableIndustries(result.availableIndustries);
        if (result.availableTopics) setAvailableTopics(result.availableTopics);
        if (result.availableThemes) setAvailableThemes(result.availableThemes);
        if (result.availableOrganisations) setAvailableOrganisations(result.availableOrganisations);
        if (result.availableCollaborationTypes) setAvailableCollaborationTypes(result.availableCollaborationTypes);
        if (result.availableStatuses) setAvailableStatuses(result.availableStatuses);

        // Set network and summary data
        setNetworkData(result.network);
        const transformedSummary = transformSummaryData(result.network, result.summary);
        setSummaryData(transformedSummary);
        setGlobalSummaryData(transformedSummary); // Store global summary for later restoration
        setStatistics(result.network || null);
        setDataLoaded(true);
        setAnalysisStatus('Analysis complete');
        setSelectedNodeIds(new Set());
        selectedNodeIdsRef.current = new Set();
        setSelectedNodes([]);
        setNeighborNodeIds(new Set());

        // Fetch heatmap data in background (non-blocking)
        // Note: minLinkWeight is reset to 1 on new data, so no slider context needed here
        authFetch(`${API_BASE_URL}/api/heatmap`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filters, view: topicViewRef.current || undefined, xAxis: heatmapAxesRef.current.x, yAxis: heatmapAxesRef.current.y })
        })
          .then(res => res.ok ? res.json() : null)
          .then(hm => { if (hm) { setHeatmapData(hm); setGlobalHeatmapData(hm); } })
          .catch(err => console.warn('Heatmap fetch failed:', err));

        // Fetch UMAP data in background (non-blocking)
        authFetch(`${API_BASE_URL}/api/umap`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filters, view: topicViewRef.current || undefined })
        })
          .then(res => res.ok ? res.json() : null)
          .then(um => { if (um) { setUmapData(um); setGlobalUmapData(um); } })
          .catch(err => console.warn('UMAP fetch failed:', err));
      }
    } catch (error) {
      console.error('Error running analysis:', error);
      setError(`Failed to run analysis: ${error.message}`);
      return null;
    } finally {
      setLoading(false);
      stopProgressPolling();
    }
    return null;
  };
  
  // Start polling for progress updates
  const startProgressPolling = () => {
    // Clear any existing interval
    if (progressPollingInterval) {
      clearInterval(progressPollingInterval);
    }
    
    // Set new polling interval (every 1 second)
    const intervalId = setInterval(async () => {
      try {
        // Use relative path
        const response = await authFetch(`${API_BASE_URL}/api/progress`);
        if (response.ok) {
          const progress = await response.json();
          setAnalysisStatus(`${progress.status}: ${progress.message} (${progress.progress}%)`);
          
          // If complete or error, stop polling
          if (['complete', 'error', 'ready'].includes(progress.status)) {
            stopProgressPolling();
          }
        }
      } catch (error) {
        console.error('Error polling progress:', error);
      }
    }, 1000);
    
    setProgressPollingInterval(intervalId);
  };
  
  // Stop polling for progress updates
  const stopProgressPolling = () => {
    if (progressPollingInterval) {
      clearInterval(progressPollingInterval);
      setProgressPollingInterval(null);
    }
  };
  
  // Clean up polling interval on component unmount
  useEffect(() => {
    return () => {
      if (progressPollingInterval) {
        clearInterval(progressPollingInterval);
      }
    };
  }, [progressPollingInterval]);

  // Handler for slider changes - updates the filters state
  const handleSliderChange = (param) => (event) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [param]: parseInt(event.target.value, 10) // Ensure value is integer
    }));
  };

  // Handler for toggle changes (like the custom label checkbox)
  const handleToggleChange = (param) => (event) => {
    const newValue = event.target.checked; // Get the new boolean value
    console.log(`Setting ${param} to:`, newValue); // *** Add console log here ***
    setFilters(prevFilters => ({
      ...prevFilters,
      [param]: newValue // Use checked status for boolean toggles
    }));
  };

  // Function to toggle theme
  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'autumn' ? 'dark' : 'autumn'));
  };

  // Get current theme colors
  const currentThemeColors = getThemeColors(theme);

  // Handle export — calls backend to generate ZIP with PNGs + CSVs
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const exportPayload = {
        ...filters,
        hideOrgLabels,
        minLinkWeight,
        features: features && features.length ? features : ['network','heatmap','umap','data','summary','export'],
        ...(visibleNodeIds ? { visibleNodeIds: Array.from(visibleNodeIds) } : {}),
        ...(selectedNodeIds.size > 0 ? {
          selectedNodeIds: Array.from(selectedNodeIds),
          selectedNeighborIds: [...neighborNodeIds]
        } : {})
      };
      const response = await authFetch(`${API_BASE_URL}/api/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(exportPayload)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Export failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'nexus_export.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setError(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Drag-to-resize handlers for summary panel
  const handleResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = false;
    dragStartXRef.current = e.clientX;
    dragStartWidthRef.current = summaryPanelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (moveE) => {
      const delta = dragStartXRef.current - moveE.clientX;
      if (!isDraggingRef.current && Math.abs(delta) > 4) {
        isDraggingRef.current = true;
      }
      if (!isDraggingRef.current) return;
      const newWidth = Math.min(800, Math.max(250, dragStartWidthRef.current + delta));
      setSummaryPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      isDraggingRef.current = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [summaryPanelWidth]);



  // Memoize graph data to prevent unnecessary graph re-renders if only selection changes
  const graphDataForRender = useMemo(() => {
      if (!networkData) return { nodes: [], links: [] };
      return {
          nodes: networkData.nodes || [],
          links: networkData.edges || [] // Map edges to links
      };
  }, [networkData]);

  // Compute max link weight for slider range
  const maxLinkWeight = useMemo(() => {
    if (!graphDataForRender.links?.length) return 1;
    return Math.max(1, ...graphDataForRender.links.map(l => l.weight || 1));
  }, [graphDataForRender]);

  // Reset minLinkWeight when new data arrives (or apply pending slider-max)
  const prevGraphDataRef = useRef(null);
  useEffect(() => {
    const prevNodes = prevGraphDataRef.current?.nodes;
    const prevLinks = prevGraphDataRef.current?.links;
    if (prevNodes !== graphDataForRender.nodes || prevLinks !== graphDataForRender.links) {
      prevGraphDataRef.current = graphDataForRender;

      // If a use-case requested slider-max, wait for real data before consuming
      if (pendingSliderMaxRef.current) {
        if (graphDataForRender.nodes.length > 0) {
          // Real data arrived — set slider to the maximum weight
          setMinLinkWeight(maxLinkWeight);
          pendingSliderMaxRef.current = false;
        }
        // Skip reset while waiting (data-clear produces empty nodes)
        return;
      }

      setMinLinkWeight(1);
    }
  }, [graphDataForRender, maxLinkWeight]);

  // Compute visible node IDs based on connection strength filter
  const visibleNodeIds = useMemo(() => {
    if (minLinkWeight <= 1 || !graphDataForRender.links?.length) return null; // null = all nodes visible
    const filteredLinks = graphDataForRender.links.filter(l => (l.weight || 1) >= minLinkWeight);
    const ids = new Set();
    filteredLinks.forEach(l => {
      const sId = typeof l.source === 'object' ? l.source.id : l.source;
      const tId = typeof l.target === 'object' ? l.target.id : l.target;
      ids.add(sId);
      ids.add(tId);
    });
    return ids;
  }, [graphDataForRender, minLinkWeight]);

  // Debounced summary + heatmap + UMAP update when connection strength filter or selection changes
  useEffect(() => {
    if (!dataLoaded || !networkData) return;

    // --- Nodes are selected: fetch node-specific summary, heatmap & UMAP ---
    if (selectedNodeIds.size > 0) {
      const currentIds = new Set(selectedNodeIds);
      const nodeIdsArray = Array.from(currentIds);
      const timeoutId = setTimeout(async () => {
        try {
          // Build request body with connection-strength context
          const nodeRequestBody = {
            filters,
            nodeIds: nodeIdsArray,
            ...(visibleNodeIds ? {
              visibleNodeIds: Array.from(visibleNodeIds),
              minLinkWeight: minLinkWeight
            } : {})
          };

          // Build a node-scoped body for heatmap & UMAP:
          // pass selectedNodeIds so backend filters proposals to these nodes only
          const nodeFilterBody = {
            filters,
            selectedNodeIds: nodeIdsArray,
            view: topicViewRef.current || undefined,
            ...(visibleNodeIds ? {
              visibleNodeIds: Array.from(visibleNodeIds),
              minLinkWeight: minLinkWeight
            } : {})
          };

          // Re-fetch node details so proposals list updates too
          const detailsResponse = await authFetch(`${API_BASE_URL}/api/nodes/multi`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(nodeRequestBody)
          });
          if (detailsResponse.ok && setsEqual(selectedNodeIdsRef.current, currentIds)) {
            const { nodes: nodesData } = await detailsResponse.json();
            setSelectedNodes((nodesData || []).map(nd => ({
              ...nd,
              id: nd.id,
              connections: Array.isArray(nd.connections) ? nd.connections : [],
              proposals: Array.isArray(nd.proposals) ? nd.proposals : [],
              statistics: nd.statistics || {}
            })));
          }

          const summaryResponse = await authFetch(`${API_BASE_URL}/api/nodes/multi/summary`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(nodeRequestBody)
          });
          if (summaryResponse.ok && setsEqual(selectedNodeIdsRef.current, currentIds)) {
            const nodeSummaryData = await summaryResponse.json();
            setSummaryData(transformSummaryData(null, nodeSummaryData));
          }

          // Fetch node-scoped heatmap
          if (setsEqual(selectedNodeIdsRef.current, currentIds)) {
            try {
              const hmRes = await authFetch(`${API_BASE_URL}/api/heatmap`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(nodeFilterBody)
              });
              if (hmRes.ok && setsEqual(selectedNodeIdsRef.current, currentIds)) {
                setHeatmapData(await hmRes.json());
              }
            } catch (e) { console.warn('Node heatmap fetch failed:', e); }
          }

          // Fetch node-scoped UMAP
          if (setsEqual(selectedNodeIdsRef.current, currentIds)) {
            try {
              const umRes = await authFetch(`${API_BASE_URL}/api/umap`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(nodeFilterBody)
              });
              if (umRes.ok && setsEqual(selectedNodeIdsRef.current, currentIds)) {
                setUmapData(await umRes.json());
              }
            } catch (e) { console.warn('Node UMAP fetch failed:', e); }
          }
        } catch (err) {
          console.warn('Failed to re-fetch node data:', err);
        }
      }, 400);
      return () => clearTimeout(timeoutId);
    }

    // --- No node selected ---
    if (visibleNodeIds === null) {
      // All nodes visible — restore global data
      if (globalSummaryData) setSummaryData(globalSummaryData);
      if (globalHeatmapData) setHeatmapData(globalHeatmapData);
      if (globalUmapData) setUmapData(globalUmapData);
      return;
    }

    // Slider is active (no selection) — fetch filtered summary, heatmap & UMAP
    const timeoutId = setTimeout(async () => {
      const body = {
        filters,
        visibleNodeIds: Array.from(visibleNodeIds),
        minLinkWeight,
        view: topicViewRef.current || undefined
      };

      // Summary
      try {
        const nodeIds = Array.from(visibleNodeIds);
        const response = await authFetch(`${API_BASE_URL}/api/summary-by-nodes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nodeIds, filters, minLinkWeight })
        });
        if (response.ok && selectedNodeIdsRef.current.size === 0) {
          const filteredSummary = await response.json();
          setSummaryData(transformSummaryData(null, filteredSummary));
        }
      } catch (err) {
        console.warn('Failed to fetch filtered summary:', err);
      }

      // Heatmap
      try {
        const hmRes = await authFetch(`${API_BASE_URL}/api/heatmap`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...body, xAxis: heatmapAxes.x, yAxis: heatmapAxes.y })
        });
        if (hmRes.ok && selectedNodeIdsRef.current.size === 0) {
          setHeatmapData(await hmRes.json());
        }
      } catch (err) {
        console.warn('Heatmap re-fetch failed:', err);
      }

      // UMAP
      try {
        const umRes = await authFetch(`${API_BASE_URL}/api/umap`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        if (umRes.ok && selectedNodeIdsRef.current.size === 0) {
          setUmapData(await umRes.json());
        }
      } catch (err) {
        console.warn('UMAP re-fetch failed:', err);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleNodeIds, dataLoaded, selectedNodeIds, neighborNodeIds]);

  return (
    // Add a class to the main div based on the theme
    <div className={`App theme-${theme}`}>
      <main className="app-content">
        <div className="filter-panel-container">
          <FilterPanel
            filters={filters}
            setFilters={setFilters}
            onRunAnalysis={handleRunAnalysis}
            loading={loading}
            analysisStatus={analysisStatus}
            theme={theme}
            toggleTheme={toggleTheme}
            themeColors={currentThemeColors}
            availableDatePoints={availableDatePoints}
            availableIndustries={availableIndustries}
            availableTopics={availableTopics}
            availableThemes={availableThemes}
            availableOrganisations={availableOrganisations}
            availableCollaborationTypes={availableCollaborationTypes}
            availableStatuses={availableStatuses}
            dynamicFilterOptions={dynamicFilterOptions}
            schemaConfig={schemaConfig}
            onSignOut={onAuthFailure}
            onHelpSection={openHelp}
          />
        </div>
        
        <div className={dataLoaded ? "visualization-container-active" : "visualization-container"}>
          {loading ? (
            <div className="loading-indicator">
              <div className="spinner"></div>
              <p>Loading data...</p>
            </div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : !fileUploaded ? (
            <div className="welcome-message">
              <p>lead with evidence</p>
              <button 
                className="big-run-button"
                onClick={handleRunAnalysis}
                disabled={loading}
              >
                {loading ? 'RUNNING...' : 'start'}
              </button>
            </div>
          ) : !dataLoaded ? (
            <div className="welcome-message">
              <p>lead with evidence</p>
              <button 
                className="big-run-button"
                onClick={handleRunAnalysis}
                disabled={loading}
              >
                {loading ? 'RUNNING...' : 'start'}
              </button>
            </div>
          ) : (
            <>
              {/* Center column: Tabbed Network / Heatmap view */}
              <div className="network-graph-wrapper">
                {/* Tab switcher */}
                <div className="viz-tab-bar">
                  {hasFeature('overview') && (
                  <button
                    className={`viz-tab-btn${activeVizTab === 'overview' ? ' active' : ''}`}
                    onClick={() => setActiveVizTab('overview')}
                  >
                    Overview
                  </button>
                  )}
                  {hasFeature('network') && (
                  <button
                    className={`viz-tab-btn${activeVizTab === 'network' ? ' active' : ''}`}
                    onClick={() => setActiveVizTab('network')}
                  >
                    Network
                  </button>
                  )}
                  {(hasFeature('heatmap') || hasFeature('umap')) && (
                  <button
                    className={`viz-tab-btn${activeVizTab === 'heatmap' ? ' active' : ''}`}
                    onClick={() => setActiveVizTab('heatmap')}
                  >
                    Landscape
                  </button>
                  )}
                  {hasFeature('data') && (
                  <button
                    className={`viz-tab-btn${activeVizTab === 'data' ? ' active' : ''}`}
                    onClick={() => setActiveVizTab('data')}
                  >
                    Data
                  </button>
                  )}
                  {hasFeature('strategy') && (
                  <button
                    className={`viz-tab-btn${activeVizTab === 'strategy' ? ' active' : ''}`}
                    onClick={() => setActiveVizTab('strategy')}
                  >
                    Strategy
                  </button>
                  )}
                  {hasFeature('insight') && (
                  <button
                    className={`insight-toggle${insightEnabled ? ' active' : ''}`}
                    onClick={() => setInsightEnabled(v => !v)}
                    title={insightEnabled ? 'Disable insight layer' : 'Enable insight layer'}
                  >
                    insight
                  </button>
                  )}
                  <div className="viz-tab-actions">
                    {hasFeature('export') && (
                    <button
                      className={`viz-action-btn${exporting ? ' exporting' : ''}`}
                      onClick={handleExport}
                      disabled={exporting}
                      title="Export all plots and data as ZIP"
                    >
                      {exporting ? (
                        <><span className="export-spinner"></span> Exporting…</>
                      ) : (
                        'Export'
                      )}
                    </button>
                    )}
                    {hasFeature('help') && (
                    <button
                      className="viz-action-btn help-btn"
                      onClick={() => { setHelpActiveSection(null); setHelpOpen(true); }}
                      title="Manual & Use cases"
                    >
                      ?
                    </button>
                    )}
                  </div>
                </div>

                {/* Global topic-slice selector — shown whenever the backend advertises views */}
                {topicViews.length > 0 && (
                  <div className="topic-view-selector" style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '6px 12px',
                    background: currentThemeColors?.panelBg || '#f5f0e6',
                    color: currentThemeColors?.textPrimary || '#2a2118',
                    borderBottom: `1px solid ${currentThemeColors?.borderColor || '#d8cfbe'}`,
                    fontSize: 12
                  }}>
                    <label htmlFor="topic-view-select" style={{ fontWeight: 600 }}>
                      {topicLabelSingular} slice:
                    </label>
                    <select
                      id="topic-view-select"
                      value={topicView}
                      onChange={(e) => setTopicView(e.target.value)}
                      style={{
                        background: '#fff',
                        color: '#2a2118',
                        border: `1px solid ${currentThemeColors?.borderColor || '#bba98b'}`,
                        borderRadius: 4,
                        padding: '4px 8px',
                        fontSize: 12,
                        cursor: 'pointer',
                        minWidth: 200
                      }}
                      title={topicViews.find(v => v.id === topicView)?.description || 'Show all topics'}
                    >
                      <option value="">All {topicLabelPlural.toLowerCase()} ({umapData?.topics?.length || heatmapData?.columns?.length || '—'})</option>
                      {topicViews.map(v => (
                        <option key={v.id} value={v.id} title={v.description}>{v.label}</option>
                      ))}
                    </select>
                    <span style={{ opacity: 0.75, fontStyle: 'italic', flex: 1 }}>
                      {topicView
                        ? topicViews.find(v => v.id === topicView)?.description
                        : `Restrict the ${topicLabelSingular.toLowerCase()} axis to a curated slice (applies to all views).`}
                    </span>
                    {umapData?.points && (
                      <span style={{ opacity: 0.7, whiteSpace: 'nowrap' }}>
                        {umapData.points.length.toLocaleString()} pts · {umapData.topics?.length || 0} {topicLabelPlural.toLowerCase()}
                      </span>
                    )}
                  </div>
                )}

                {/* Overview view */}
                {activeVizTab === 'overview' && (
                  <OverviewPanel
                    apiBaseUrl={API_BASE_URL}
                    authFetch={authFetch}
                    filters={filters}
                    topicView={topicView}
                    schemaConfig={schemaConfig}
                    onTopicClick={(name) => {
                      setFilters((prev) => ({ ...prev, topics: [name] }));
                    }}
                    onIndustryClick={(name) => {
                      setFilters((prev) => ({ ...prev, industry: [name] }));
                    }}
                    onThemeClick={(name) => {
                      setFilters((prev) => ({ ...prev, themes: [name] }));
                    }}
                    onDocClick={({ filename, paraIdx }) => openDocument(filename, paraIdx)}
                    onHelpSection={openHelp}
                  />
                )}

                {/* Network view */}
                {activeVizTab === 'network' && (
                  <>
                    <NetworkGraph
                      graphData={graphDataForRender}
                      onNodeSelect={handleNodeSelect}
                      selectedNodeIds={selectedNodeIds}
                      neighborNodeIds={neighborNodeIds}
                      themeColors={currentThemeColors}
                      hideOrgLabels={hideOrgLabels}
                      setHideOrgLabels={setHideOrgLabels}
                      minLinkWeight={minLinkWeight}
                      setMinLinkWeight={setMinLinkWeight}
                      maxLinkWeight={maxLinkWeight}
                      onHelpSection={openHelp}
                      showIndustryOverlay={showIndustryOverlay}
                      setShowIndustryOverlay={setShowIndustryOverlay}
                      theme={theme}
                      networkMeta={schemaConfig?.network}
                      networkView={filters.networkView || schemaConfig?.network?.defaultView}
                      onNetworkViewChange={(viewId) => handleNetworkControlChange({ networkView: viewId })}
                      peopleAxis={filters.peopleAxis}
                      onPeopleAxisChange={(axis) => handleNetworkControlChange({ peopleAxis: axis })}
                      requireCluster={filters.requireCluster}
                      onRequireClusterChange={(val) => handleNetworkControlChange({ requireCluster: val })}
                    />
                    {/* Insight Cards for network tab */}
                    {hasFeature('insight') && insightEnabled && dataLoaded && (
                      <InsightCards
                        tab="network"
                        filters={filters}
                        topicView={topicView}
                        selectedNodes={selectedNodeIds}
                        authFetch={authFetch}
                        onAskAbout={(title, body, question) => {
                          setChatDrawerOpen(true);
                          if (window.__chatDrawerAskAbout) {
                            window.__chatDrawerAskAbout(title, body, 'network', question);
                          }
                        }}
                      />
                    )}
                    {/* Node Details overlay */}
                    {selectedNodes.length > 0 && (
                      <div className="node-detail-overlay-container">
                        <NodeDetail
                          nodes={selectedNodes}
                          selectedNodeIds={selectedNodeIds}
                          onClose={handleClearNodeSelection}
                          onDeselectNode={handleDeselectNode}
                          themeColors={currentThemeColors}
                          theme={theme}
                          minLinkWeight={minLinkWeight}
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Heatmap + UMAP view */}
                {activeVizTab === 'heatmap' && (
                  <div className="heatmap-umap-split">
                    {/* Heatmap section */}
                    {hasFeature('heatmap') && (
                    <div className={`heatmap-split-top${collapsedSection === 'heatmap' ? ' collapsed' : ''}${collapsedSection === 'umap' ? ' expanded' : ''}`}>
                      <div className="split-section-header" onClick={() => setCollapsedSection(collapsedSection === 'heatmap' ? null : 'heatmap')}>
                        <span className="split-section-toggle">{collapsedSection === 'heatmap' ? '▶' : '▼'}</span>
                        <span>Heatmap</span>
                        {hasFeature('help') && (
                        <button
                          className="help-info-icon"
                          onClick={(e) => { e.stopPropagation(); setHelpActiveSection('heatmap'); setHelpOpen(true); }}
                          title="Learn about Heatmap"
                          style={{ marginLeft: 'auto' }}
                        >
                          ⓘ
                        </button>
                        )}
                      </div>
                      {collapsedSection !== 'heatmap' && (
                        <div className="split-section-content" style={{ position: 'relative' }}>
                          <HeatmapPanel
                            data={heatmapData}
                            themeColors={currentThemeColors}
                            theme={theme}
                            recordLabel={schemaConfig?.labels?.record || 'Record'}
                            axes={heatmapAxes}
                            axisOptions={(() => {
                              if (schemaConfig?.heatmapAxes?.length) return schemaConfig.heatmapAxes;
                              const labels = (schemaConfig && schemaConfig.labels) || {};
                              const facets = (schemaConfig && schemaConfig.facets) || [];
                              const opts = [
                                { value: 'industry', label: labels.industry || 'Industry' },
                                { value: 'topic', label: labels.topic || 'Topic' },
                              ];
                              if (facets.includes('themes')) opts.push({ value: 'themes', label: labels.themes || 'Themes' });
                              return opts;
                            })()}
                            onAxisChange={(newAxes) => {
                              setHeatmapAxes(newAxes);
                              authFetch(`${API_BASE_URL}/api/heatmap`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ filters, view: topicView || undefined, xAxis: newAxes.x, yAxis: newAxes.y })
                              })
                                .then(r => r.ok ? r.json() : null)
                                .then(hm => { if (hm) { setHeatmapData(hm); setGlobalHeatmapData(hm); } })
                                .catch(err => console.warn('Heatmap axis refetch failed:', err));
                            }}
                            onCellClick={(hasFeature('viewer') || hasFeature('data'))
                              ? (cell) => {
                                  // Heatmap axes are user-selectable. If one axis is the topic
                                  // and the document viewer exists, open the ranked-documents
                                  // picker (paragraph-grain corpora); otherwise list the rows
                                  // behind the cell in a scoped Data table.
                                  const yAxis = heatmapAxes.y;
                                  const xAxis = heatmapAxes.x;
                                  const valueFor = (axis) => axis === yAxis ? cell.row : (axis === xAxis ? cell.column : null);
                                  const topic = valueFor('topic');
                                  const industry = valueFor('industry');
                                  if (hasFeature('viewer') && topic) {
                                    openTopicDocuments({ industry, topic });
                                    return;
                                  }
                                  if (!hasFeature('data')) return;
                                  const opts = schemaConfig?.heatmapAxes || [];
                                  const labelOf = (k) => opts.find(o => o.value === k)?.label || k;
                                  setCellRecordsScope({
                                    yAxis, yValue: cell.row, yLabel: labelOf(yAxis),
                                    xAxis, xValue: cell.column, xLabel: labelOf(xAxis),
                                  });
                                }
                              : undefined}
                          />
                          {hasFeature('insight') && insightEnabled && dataLoaded && (
                            <InsightCards
                              tab="heatmap"
                              filters={filters}
                              topicView={topicView}
                              selectedNodes={selectedNodeIds}
                              authFetch={authFetch}
                              onOpenDocument={hasFeature('viewer') ? openDocument : undefined}
                              onOpenRepresentativeDoc={hasFeature('viewer') ? openTopicDocuments : undefined}
                              onAskAbout={(title, body, question) => {
                                setChatDrawerOpen(true);
                                if (window.__chatDrawerAskAbout) {
                                  window.__chatDrawerAskAbout(title, body, 'heatmap', question);
                                }
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                    )}
                    {/* UMAP section */}
                    {hasFeature('umap') && (
                    <div className={`heatmap-split-bottom${collapsedSection === 'umap' ? ' collapsed' : ''}${collapsedSection === 'heatmap' ? ' expanded' : ''}`}>
                      <div className="split-section-header" onClick={() => setCollapsedSection(collapsedSection === 'umap' ? null : 'umap')}>
                        <span className="split-section-toggle">{collapsedSection === 'umap' ? '▶' : '▼'}</span>
                        <span>UMAP Projection</span>
                        {hasFeature('help') && (
                        <button
                          className="help-info-icon"
                          onClick={(e) => { e.stopPropagation(); setHelpActiveSection('umap'); setHelpOpen(true); }}
                          title="Learn about UMAP"
                          style={{ marginLeft: 'auto' }}
                        >
                          ⓘ
                        </button>
                        )}
                      </div>
                      {collapsedSection !== 'umap' && (
                        <div className="split-section-content" style={{ position: 'relative' }}>
                          <UmapPanel
                            data={umapData}
                            themeColors={currentThemeColors}
                            theme={theme}
                            labels={schemaConfig?.labels}
                            onPointClick={hasFeature('viewer')
                              ? (point, event) => {
                                  // Shift-click on a UMAP dot opens the Topic→Documents
                                  // picker for that point's topic (and industry).
                                  // Plain click opens the specific paragraph the dot
                                  // represents — spatial-honest: the dot IS that paragraph.
                                  const wantsModal = event && event.shiftKey;
                                  if (wantsModal && point && point.topic) {
                                    openTopicDocuments({ industry: point.industry, topic: point.topic });
                                    return;
                                  }
                                  if (point.filename) {
                                    openDocument(point.filename, point.paraIdx ?? null);
                                  } else if (point && point.topic) {
                                    openTopicDocuments({ industry: point.industry, topic: point.topic });
                                  } else {
                                    openRepresentativeDoc({ industry: point.industry, topic: point.topic });
                                  }
                                }
                              : undefined}
                          />
                          {hasFeature('insight') && insightEnabled && dataLoaded && (
                            <InsightCards
                              tab="umap"
                              filters={filters}
                              topicView={topicView}
                              selectedNodes={selectedNodeIds}
                              authFetch={authFetch}
                              onOpenDocument={hasFeature('viewer') ? openDocument : undefined}
                              onOpenRepresentativeDoc={hasFeature('viewer') ? openTopicDocuments : undefined}
                              onAskAbout={(title, body, question) => {
                                setChatDrawerOpen(true);
                                if (window.__chatDrawerAskAbout) {
                                  window.__chatDrawerAskAbout(title, body, 'umap', question);
                                }
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                )}

                {/* Data viewer tab */}
                {activeVizTab === 'data' && (
                  <DataViewerPanel
                    filters={filters}
                    visibleNodeIds={visibleNodeIds}
                    minLinkWeight={minLinkWeight}
                    selectedNodeIds={selectedNodeIds}
                    neighborNodeIds={neighborNodeIds}
                    authFetch={authFetch}
                    apiBaseUrl={API_BASE_URL}
                    themeColors={currentThemeColors}
                    theme={theme}
                    onHelpSection={openHelp}
                    onOpenDocument={hasFeature('viewer') ? openDocument : undefined}
                  />
                )}

                {/* Strategy document tab */}
                {activeVizTab === 'strategy' && hasFeature('strategy') && (
                  <StrategyPanel authFetch={authFetch} />
                )}

                {/* Insight Cards for landscape tab — now split into each section above */}

                <div className="app-disclaimer">
                  This dashboard uses Natural Language Processing to extract topics and assign classifications. AI-generated content may contain errors — please verify.
                </div>
              </div>

              {/* Right column: Summary Panel with resize handle */}
              {hasFeature('summary') && (
              <>
              {summaryCollapsed ? (
              <div className="summary-collapsed-strip" onClick={() => setSummaryCollapsed(false)} title="Expand summary panel">
                <span className="summary-collapsed-chevron">◀</span>
                <span className="summary-collapsed-label">Summary</span>
              </div>
              ) : (
              <>
              <div className="resize-handle" onMouseDown={handleResizeMouseDown} onDoubleClick={() => setSummaryCollapsed(true)} title="Drag to resize · Double-click to collapse" />
              <div className="summary-panel-container" style={{ width: summaryPanelWidth, minWidth: 250, maxWidth: 800 }}>
                <ErrorBoundary fallbackMessage="Summary panel encountered an error">
                  <SummaryPanel 
                    data={summaryData}
                    statistics={statistics}
                    networkData={networkData}
                    themeColors={currentThemeColors}
                    theme={theme}
                    schemaConfig={schemaConfig}
                    onHelpSection={openHelp}
                  />
                </ErrorBoundary>
                {/* Insight Cards for summary panel */}
                {hasFeature('insight') && insightEnabled && dataLoaded && (
                  <InsightCards
                    tab="summary"
                    filters={filters}
                    topicView={topicView}
                    selectedNodes={selectedNodeIds}
                    authFetch={authFetch}
                    onAskAbout={(title, body, question) => {
                      setChatDrawerOpen(true);
                      if (window.__chatDrawerAskAbout) {
                        window.__chatDrawerAskAbout(title, body, 'summary', question);
                      }
                    }}
                  />
                )}
              </div>
              </>
              )}
              </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Insight: Chat Drawer */}
      {hasFeature('insight') && insightEnabled && dataLoaded && (
        <ChatDrawer
          isOpen={chatDrawerOpen}
          onToggle={setChatDrawerOpen}
          tab={activeVizTab}
          filters={filters}
          topicView={topicView}
          selectedNodes={selectedNodeIds}
          authFetch={authFetch}
          isFront={frontLayer === 'chat'}
          onFocus={() => setFrontLayer('chat')}
          onOpenDocument={hasFeature('viewer') ? ({ filename, paraIdx }) => openDocument(filename, paraIdx) : undefined}
        />
      )}

      {/* Source Document Viewer (overlay, opt-in via FEATURES=viewer) */}
      {hasFeature('viewer') && viewerDoc && (
        <DocumentViewer
          filename={viewerDoc.filename}
          anchorParaIdx={viewerDoc.paraIdx}
          onClose={() => setViewerDoc(null)}
          authFetch={authFetch}
          isFront={frontLayer === 'doc'}
          coexist={hasFeature('insight') && insightEnabled && chatDrawerOpen}
          onFocus={() => setFrontLayer('doc')}
        />
      )}

      {/* Topic → Documents picker (ranked list of docs for a topic/industry slice) */}
      {hasFeature('viewer') && topicDocsRequest && (
        <TopicDocumentsPanel
          topic={topicDocsRequest.topic}
          industry={topicDocsRequest.industry}
          topicLabel={topicDocsRequest.topic}
          industryLabel={topicDocsRequest.industry}
          fetcher={topicDocsFetcher}
          onOpenDocument={(filename, paraIdx) => {
            setTopicDocsRequest(null);
            openDocument(filename, paraIdx);
          }}
          onClose={() => setTopicDocsRequest(null)}
        />
      )}

      {hasFeature('data') && cellRecordsScope && (
        <CellRecordsModal
          scope={cellRecordsScope}
          recordLabel={schemaConfig?.labels?.record || 'Record'}
          onClose={() => setCellRecordsScope(null)}
          filters={filters}
          authFetch={authFetch}
          apiBaseUrl={API_BASE_URL}
          themeColors={currentThemeColors}
          theme={theme}
          onOpenDocument={hasFeature('viewer') ? openDocument : undefined}
        />
      )}

      {/* Help Panel — only mounted when the 'help' feature is enabled */}
      {hasFeature('help') && (
      <HelpPanel
        isOpen={helpOpen}
        onClose={() => { setHelpOpen(false); setHelpActiveSection(null); }}
        activeSection={helpActiveSection}
        features={features}
        schemaConfig={schemaConfig}
        onLoadUseCase={(filterPreset, options = {}) => {
          // Apply the use-case filter preset
          const merged = { ...filters, ...filterPreset };
          // If the use case needs full cycle range, set timeFrame to all available
          if (!filterPreset.timeFrame && availableDatePoints.length > 0) {
            merged.timeFrame = [
              availableDatePoints[0],
              availableDatePoints[availableDatePoints.length - 1]
            ];
          }
          // Set flag BEFORE updating filters — the reset effect will pick it up
          if (options.connectionStrength === 'max') {
            pendingSliderMaxRef.current = true;
          }
          // Update filters — the debounced useEffect([filters]) will run handleRunAnalysis
          setFilters(merged);
        }}
        onSetCycleRange={() => {
          // Set cycle to full range using string cycle names
          if (availableDatePoints.length > 0) {
            setFilters(prev => ({
              ...prev,
              timeFrame: [
                availableDatePoints[0],
              availableDatePoints[availableDatePoints.length - 1]
              ]
            }));
          }
        }}
      />
      )}
    </div>
  );
}

export default App;